import React, { useState } from 'react';

/**
 * ImageDisplay - Displays an image with optional configuration
 * 
 * @param {Object} props
 * @param {Object} props.feature - Feature object from RuleEngine
 * @param {string} props.value - Image URL or base64 encoded image data
 * @param {string} props.size - Image size ('small', 'medium', 'large', 'thumbnail')
 * @param {string} props.className - Additional CSS classes
 */
const ImageDisplay = ({ 
    feature, 
    value,
    size = 'medium',
    className = ''
}) => {
    const displayText = feature?.property?.displayText || feature?.name || 'Image';
    
    // Determine image dimensions based on size
    const getDimensions = () => {
        switch (size.toLowerCase()) {
            case 'small':
            case 'thumbnail':
                return { width: '40px', height: '40px' };
            case 'medium':
                return { width: '100px', height: '100px' };
            case 'large':
                return { width: '200px', height: '200px' };
            default:
                return { width: '100px', height: '100px' };
        }
    };
    
    const dimensions = getDimensions();
    
    // Check if value is a URL or base64 data
    const isUrl = value && (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/'));
    const isBase64 = value && value.startsWith('data:image/');
    
    // Get image source
    const imageSrc = isUrl ? value : isBase64 ? value : null;
    
    // Placeholder styling
    const placeholderStyle = {
        width: dimensions.width,
        height: dimensions.height,
        backgroundColor: 'var(--bg-secondary)',
        border: '2px dashed var(--border)',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.75rem',
        color: 'var(--text-secondary)',
        overflow: 'hidden'
    };
    
    const imageStyle = {
        width: dimensions.width,
        height: dimensions.height,
        objectFit: 'contain',
        border: '1px solid var(--border)',
        borderRadius: '4px',
        backgroundColor: 'var(--bg-primary)',
        display: 'block'
    };
    
    // Fallback image on error
    const [imageError, setImageError] = useState(false);
    
    const handleImageError = () => {
        setImageError(true);
    };
    
    return (
        <div 
            className={`image-display-wrapper ${className}`}
            style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '8px',
                alignItems: 'flex-start'
            }}
        >
            <label style={{
                fontSize: '0.85rem',
                fontWeight: 600,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <span>{displayText}</span>
                {feature?.property?.isOptional !== false && (
                    <span style={{ color: 'var(--error)', fontSize: '0.75rem' }}>*</span>
                )}
            </label>
            
            <div style={{ position: 'relative' }}>
                {imageSrc && !imageError ? (
                    <img
                        src={imageSrc}
                        alt={displayText}
                        style={imageStyle}
                        onError={handleImageError}
                    />
                ) : (
                    <div style={placeholderStyle}>
                        <span>No Image</span>
                    </div>
                )}
                
                {/* Size indicator */}
                <span style={{
                    position: 'absolute',
                    bottom: '-20px',
                    right: 0,
                    fontSize: '0.65rem',
                    color: 'var(--text-secondary)',
                    background: 'rgba(0, 0, 0, 0.7)',
                    padding: '2px 4px',
                    borderRadius: '2px'
                }}>
                    {size}
                </span>
            </div>
            
            {feature?.property?.description && (
                <span style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    fontStyle: 'italic'
                }}>
                    {feature.property.description}
                </span>
            )}
            
            {/* Show URL if available */}
            {imageSrc && !imageError && (
                <a
                    href={imageSrc}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        fontSize: '0.7rem',
                        color: 'var(--accent)',
                        textDecoration: 'none'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    View full image
                </a>
            )}
        </div>
    );
};

export default ImageDisplay;
